import { Injectable } from "@angular/core";

import { AppState } from "../../core/app-state";

import { HttpClient } from "@angular/common/http";
import { asyncScheduler, first, firstValueFrom, forkJoin, timer } from "rxjs";
import { activity_status, ScoutPoint } from "../../core/models";
import { ScoutTaskDoc } from "../../core/schema/scout-task-schema";
import {
  extractPathNumberFromPathId,
  ScoutPointChange,
  ScoutPointInfo,
  ScoutTaskService,
  scoutPathNumberToTestPathName,
} from "../../core/services";
import { RestClient } from "../../core/services/rest-client";
import {
  VolunteerTaskPointRecord,
  VolunteerTaskPointViewRecord,
} from "../models/volunteer-task-point-record";
import { BsModalService } from "ngx-bootstrap/modal";
import { ConfirmModalComponent } from "../../shared/components/confirm-modal/confirm-modal.component";
import { VolunteerTaskPointService } from "./volunteer-task-point.service";
import { FesRutTestPointTestParams } from "../models";
import { TableViewQueryResponse } from "../../shared/components/table-view/table-view-component-base";

@Injectable({ providedIn: "root" })
export class VolunteerTaskPointViewService extends RestClient<VolunteerTaskPointViewRecord> {
  constructor(
    public appState: AppState,
    http: HttpClient,
    public scoutService: ScoutTaskService,
    protected volunteerTaskPointService: VolunteerTaskPointService,
    protected bsModalService: BsModalService
  ) {
    super(http, "volunteerTaskPointView", appState);
  }

  async handleScoutPointChange({
    scoutTask,
    scoutPath,
    scoutPoint,
    changes,
  }: ScoutPointChange) {
    if (!("disabled" in changes)) {
      return;
    }
    const scoutPtId = scoutPoint.pointId || scoutPoint.exPointId;
    const title = `Volunteer Test Points for ${scoutPtId} and scout pathPath #${scoutPath.pathNumber} `;
    const plsWaitModalRef = this.bsModalService.show(ConfirmModalComponent, {
      animated: false, // IMPORTANT.  keep this turned off because if animation is not complete before calling modal.hide(), it will not be hidden
      backdrop: true,    
      ignoreBackdropClick: true,
      class: 'modal-lg modal-edge-shadow',
      initialState: {
        noBtnText: null,
        yesBtnText: null,
        title,
        question:
          '<i class="fa fa-spinner fa-spin mr-2"></i>Please wait... searching for volunteer test point(s) with status ' +
          (scoutPoint.disabled
            ? "'NOT STARTED' or 'IN PROGRESS'"
            : "'NOT USING'") +
          `.`,
      },
    });
    let volTaskPoints: TableViewQueryResponse<VolunteerTaskPointViewRecord>;

    try {
      volTaskPoints = await this.find({
        filters: [
          {
            key: "pointId",
            mode: "=",
            value: scoutPoint.pointId || scoutPoint.exPointId,
          },
          { key: "buildingId", mode: "=", value: scoutTask.buildingId },
          {
            key: "statusName",
            mode: "~*",
            value: scoutPoint.disabled
              ? `${activity_status.Names.NOT_STARTED}|${activity_status.Names.IN_PROGRESS}`
              : `${activity_status.Names.NOT_USING}`,
          },
          {
            key: "pathId",
            mode: "=",
            value: scoutPathNumberToTestPathName(
              scoutTask.buildingId,
              scoutPath.pathNumber
            ),
          },
        ],
      });
    } catch ( error: any ) {
      console.error(error);
      throw error;
    } finally {
      asyncScheduler.schedule(()=>plsWaitModalRef.hide(),800);
      await firstValueFrom(plsWaitModalRef.onHidden);
    }
    const doTaskPointsStatusChange = async (): Promise<void> => {
      console.log("doTaskPointsStatusChange()", {
        scoutPointDisabled: scoutPoint.disabled,
      });
      const promises = await Promise.all(
        volTaskPoints.rows.map((volTaskPoint) =>
          this.patchTaskPoint({
            id: volTaskPoint.id,
            status: scoutPoint.disabled
              ? activity_status.Id.NOT_USING
              : volTaskPoint.lastCallTime
              ? activity_status.Id.IN_PROGRESS
              : activity_status.Id.NOT_STARTED,
          })
        )
      );
      const results = await Promise.allSettled(promises);
      const failures = results.filter((result) => result.status === "rejected");
      console.error(failures);
      this.appState.publishError(
        "Error changing test point(s) status. " +
        failures.map((failure:PromiseRejectedResult) => `${failure.reason}`), this
      );

      return;
    };
    if (volTaskPoints.rows.length === 0) {
      return;
    }
    const confirmModalRef = this.bsModalService.show(ConfirmModalComponent, {
      backdrop: true,
      ignoreBackdropClick: true,
      class: 'modal-lg modal-edge-shadow',
      initialState: {        
        onYes: doTaskPointsStatusChange,
        title,
        question:
          "Change volunteer test point(s) status " +
          (scoutPoint.disabled
            ? "from 'NOT STARTED' or 'IN PROGRESS' to 'NOT USING'"
            : "from 'NOT USING' to 'NOT STARTED' or 'IN PROGRESS'") +
          ` (${volTaskPoints.rows.length} test points)?`,
      },
    });
    await firstValueFrom(confirmModalRef.onHidden);
  }

  async buildScoutInfoForTaskPoints(recs: VolunteerTaskPointViewRecord[]) {
    const recsWithoutScoutId = recs.filter((r) => !r.scoutId);
    if (recsWithoutScoutId.length) {
      await this.updateScoutIds(recsWithoutScoutId);
    }
    return await this.getScoutInfoForTaskPoints(recs);
  }

  //
  // This will assign rec.scoutId in memory if found by building and path (no db save)
  //
  async updateScoutIds(recs: VolunteerTaskPointViewRecord[]) {
    const uniqueBuildingIds = [...new Set(recs.map((r) => r.buildingId))];
    const scoutsByBuilding: { [buildingId: string]: ScoutTaskDoc[] } = {};

    const scoutQueries = uniqueBuildingIds.map((buildingId) =>
      this.scoutService.rxCollection
        .find({
          selector: { buildingId },
        })
        .exec()
        .then((scouts) => (scoutsByBuilding[buildingId] = scouts))
    );
    if (scoutQueries.length > 0) {
      await firstValueFrom(forkJoin(scoutQueries));
    }
    recs.forEach((rec) => {
      const pathNumber = extractPathNumberFromPathId(rec.pathId);
      const hasPathNumber = (scout: ScoutTaskDoc) => {
        return scout?.scoutPaths.find((sp) => sp.pathNumber == pathNumber);
      };
      //TODO sort so last modified scout used if multiple found
      const scoutsForBldg =
        scoutsByBuilding[rec.buildingId].filter(hasPathNumber);
      rec.scoutId = scoutsForBldg[0]._id;
      if (scoutsForBldg.length > 1) {
        const msg = `Multiple scouts found for building ${rec.buildingId} and path ${pathNumber}`;
        console.warn(msg);
        this.publishError(msg, this, 60);
      }
    });
  }

  async getScoutInfoForTaskPoints<T extends string | number | symbol = number>(
    recs: VolunteerTaskPointViewRecord[],
    scoutCache: { [scoutId: string]: ScoutTaskDoc } = {},
    resultKeyFn: (rec: VolunteerTaskPointRecord, spt: ScoutPoint) => T = (
      rec,
      spt
    ) => rec.id as T
  ): Promise<{ [recKey in T]: ScoutPointInfo }> {
    const scoutInfoByRecKey = {} as { [recKey in T]: ScoutPointInfo };
    for (const rec of recs) {
      if (!rec.scoutId) {
        console.warn(
          `No scoutId could be resolved for volunteer task point (id:${rec.id},buildingId:${rec.buildingId})`
        );
        continue;
      }
      // console.log(
      //   `task point pathId: ${rec.pathId}, pointId: ${rec.pointId}, scoutId: ${rec.scoutId}`
      // );
      let scout = scoutCache[rec.scoutId];
      if (!scout) {
        scoutCache[rec.scoutId] = scout = await this.scoutService.getById(
          rec.scoutId
        );
      }
      const pathNumber = extractPathNumberFromPathId(rec.pathId);
      // console.log("pathNumber:", pathNumber);
      const scoutPath = scout?.scoutPaths.find(
        (sp) => sp.pathNumber == pathNumber
      );
      if (scoutPath) {
        this.scoutService.updateScoutPathContext(scoutPath, []);
        // console.dir(scoutPath);
      }
      const scoutPoint =
        scoutPath &&
        (scoutPath.scoutPoints.find((sp) => sp.pointId == rec.pointId) ||
          scoutPath.scoutPoints.find(
            (sp) => !sp.pointId && sp.exPointId == rec.pointId
          ));
      scoutInfoByRecKey[resultKeyFn(rec, scoutPoint)] = {
        scout,
        scoutPath,
        scoutPoint,
        pathId: rec.pathId,
      };
    }

    return scoutInfoByRecKey;
  }

  saveCommonName = async (args: {
    volunteerTaskPointId: number;
    commonName: string;
  }) => {
    console.log(`saveCommonName(${JSON.stringify(args)})`);
    const result: any = await firstValueFrom(
      this.http.put<VolunteerTaskPointViewRecord>(
        `${this.urlPath}/saveCommonName`,
        args,
        { observe: "response" }
      )
    );
    const rec = result.body?.data as VolunteerTaskPointViewRecord;
    console.log(JSON.stringify(rec));
    return rec;
  };

  updateCustomTestParams = async (
    taskPointId: number,
    customTestParams: FesRutTestPointTestParams
  ) => {
    const args = { taskPointId, customTestParams };
    console.log(`updateCustomTestParams(${JSON.stringify(args)})`);
    const result: any = await firstValueFrom(
      this.http.put<VolunteerTaskPointViewRecord>(
        `${this.urlPath}/updateCustomTestParams`,
        args,
        { observe: "response" }
      )
    );
    const rec = result.body?.data as VolunteerTaskPointViewRecord;
    console.log(JSON.stringify(rec));
    return rec;
  };

  patchTaskPoint = async (
    data: { id: number } & Partial<VolunteerTaskPointRecord>
  ) => {
    console.log(
      `patchTaskPoint(${JSON.stringify(data)}) (${
        data?.revisionId ? "enforcing latest revision" : "ignoring revision"
      })`
    );
    const result: any = await firstValueFrom(
      this.http.put<VolunteerTaskPointViewRecord>(
        `${this.urlPath}/patchTaskPoint`,
        data,
        { observe: "response" }
      )
    );
    const rec = result.body?.data as VolunteerTaskPointViewRecord;
    console.log(JSON.stringify(rec));
    return rec;
  };

  async promptedDeleteTaskPoint(taskPoint: VolunteerTaskPointViewRecord) {
    let deletedTaskPoint: VolunteerTaskPointRecord = null;
    let deleteConfirmed = false;
    const initialState = {
      onYes: async () => (deleteConfirmed = true),
      title: `Confirm Deletion`,
      question: `<div class="m-2 font-italic text-danger">
            <i class='text-warning fa fa-exclamation-triangle mr-1'></i>
            Delete task point for ${taskPoint?.pointId}?</div>`,
      focusOnNo: true,
      yesStyle: "btn btn-danger",
    };
    const modalRef = this.bsModalService.show(ConfirmModalComponent, {
      ignoreBackdropClick: true,
      id: 202307072,
      initialState,
    });
    await firstValueFrom(modalRef.onHidden.pipe(first()));
    if (deleteConfirmed) {
      try {
        deletedTaskPoint = await this.volunteerTaskPointService.deleteById(
          taskPoint.id
        );
        this.appState.notifySuccess(
          `Deleted task point (point: ${taskPoint.pointId}, volunteer: ${taskPoint.volunteerName})`,
          this,
          2
        );
      } catch (error: any) {
        console.error(error);
        const errorMsg = this.extractErrorMsg(error);
        if (errorMsg.indexOf("constraint") != -1) {
          return await this.promptedArchiveTaskPoint(
            taskPoint,
            "Task point cannot be deleted (in use)"
          );
        } else {
          this.publishError(error, this);
          this.appState.notifyError(errorMsg, this, 2);
        }
      }
    }
    return deletedTaskPoint;
  }

  async promptedArchiveTaskPoint(
    taskPoint: VolunteerTaskPointViewRecord,
    detailMsg: string = null
  ) {
    let archivedTaskPoint: VolunteerTaskPointViewRecord = null;
    let archiveConfirmed = false;
    const initialState = {
      onYes: async () => (archiveConfirmed = true),
      title: `Confirm Archive Status`,
      question: `<div *ngIf="detailMsg">${detailMsg}</div>
        <div class="m-2 font-italic text-danger">
        <i class='text-warning fa fa-exclamation-triangle mr-1'></i>
        Archive task point (${taskPoint.pointId}) for user ${taskPoint?.volunteerName}?</div>`,
      focusOnNo: true,
      yesStyle: "btn btn-danger",
    };
    const modalRef = this.bsModalService.show(ConfirmModalComponent, {
      ignoreBackdropClick: true,
      id: 20230707,
      initialState,
    });
    await firstValueFrom(modalRef.onHidden.pipe(first()));
    if (archiveConfirmed) {
      try {
        archivedTaskPoint = await this.patchTaskPoint({
          id: taskPoint.id,
          status: activity_status.Id.ARCHIVED,
          revisionId: taskPoint.revisionId,
        });
        this.appState.notifySuccess(
          `Archived task point (point: ${taskPoint.pointId}, volunteer: ${taskPoint.volunteerName})`,
          this,
          2
        );
      } catch (error: any) {
        const errorMsg = this.extractErrorMsg(error);
        this.publishError(error, this);
        this.appState.notifyError(errorMsg, this, 2);
      }
    }
    return archivedTaskPoint;
  }
}
